/**
 * Submodel Cleanup Utilities for UI-Model-1
 *
 * Pure functions to detect and clear stale submodel references from containers.
 * A submodel reference becomes stale when the container no longer DIRECTLY
 * holds any item of that submodel (via delete, move, or sync) — nested
 * containers carry their own bindings and don't keep a parent's alive.
 */

import type { ContainerAPI } from '../types/api.types';
import { isItemAPI } from '../types/api.types';

/**
 * Check if a container DIRECTLY holds items that reference the given submodel
 * ID via parentId. Items in nested containers do NOT count: every container
 * that holds a submodel item carries its own binding, so when a parent's last
 * direct item is removed the parent releases its binding (and header bar) even
 * while a descendant still uses that submodel — the descendant's own binding
 * takes over. (Product decision 2026-07-13; was recursive before.)
 */
export const containerHasDirectSubmodelItems = (
  container: ContainerAPI,
  submodelId: string
): boolean =>
  (container.content.items || []).some(
    item => isItemAPI(item) && item.parentId === submodelId
  );

/**
 * Walk an array of containers (bottom-up through nesting) and clear
 * `content.submodel` on any container whose submodel ID is no longer
 * referenced by any of its items.
 *
 * Returns a new array — never mutates the input.
 */
export const clearEmptySubmodelRefs = (
  containers: ContainerAPI[]
): ContainerAPI[] => {
  return containers.map(container => {
    let updated = { ...container };

    // Process nested containers first (bottom-up)
    if (updated.content.nestedContainers?.length) {
      updated = {
        ...updated,
        content: {
          ...updated.content,
          nestedContainers: clearEmptySubmodelRefs(updated.content.nestedContainers),
        },
      };
    }

    // Then check self.
    // Free style list first: drop every binding whose submodel no longer has
    // DIRECT items here, then keep the singular slot mirroring the first
    // surviving entry — so removing one submodel's fields from a mixed
    // container releases just that binding.
    if (updated.content.submodels?.length) {
      const surviving = updated.content.submodels.filter(sm =>
        containerHasDirectSubmodelItems(updated, sm.id)
      );
      if (surviving.length !== updated.content.submodels.length) {
        updated = {
          ...updated,
          content: {
            ...updated.content,
            submodels: surviving.length ? surviving : undefined,
            submodel: surviving[0],
          },
        };
      }
      return updated;
    }

    const submodelId = updated.content.submodel?.id;
    if (submodelId && !containerHasDirectSubmodelItems(updated, submodelId)) {
      updated = {
        ...updated,
        content: { ...updated.content, submodel: undefined },
      };
    }

    return updated;
  });
};
